import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { get, post, setCsrfToken } from "@/api/client";
import type { LoginResponse, Me } from "@/api/types";

interface AuthState {
  me: Me | null;
  loading: boolean;
  mfaPending: boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  verifyMfa: (code: string) => Promise<void>;
  /** SSO came back with a session that still owes its 2FA code. */
  resumeMfa: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // /auth/me hands back the session's CSRF token, so a tab that has the
      // cookie but not the token — a new tab, a restarted browser — can write
      // again instead of being stuck read-only.
      const next = await get<Me>("/auth/me");
      if (next.csrf_token) setCsrfToken(next.csrf_token);
      setMe(next);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // A 401 from any call means the session is gone; drop the user immediately
    // rather than leaving a stale shell on screen.
    const onUnauthorized = () => {
      setMe(null);
      setLoading(false);
    };
    window.addEventListener("mdc:unauthorized", onUnauthorized);
    return () => window.removeEventListener("mdc:unauthorized", onUnauthorized);
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const resp = await post<LoginResponse>("/auth/login", { email, password });
    setCsrfToken(resp.csrf_token);
    if (resp.mfa_required) {
      setMfaPending(true);
      return { mfaRequired: true };
    }
    setMfaPending(false);
    setMe(resp as Me);
    setLoading(false);
    return { mfaRequired: false };
  }, []);

  const verifyMfa = useCallback(async (code: string) => {
    const resp = await post<LoginResponse>("/auth/mfa/verify", { code });
    setCsrfToken(resp.csrf_token);
    setMfaPending(false);
    setMe(resp as Me);
  }, []);

  const resumeMfa = useCallback(() => setMfaPending(true), []);

  const logout = useCallback(async () => {
    try {
      await post("/auth/logout");
    } finally {
      setCsrfToken(null);
      setMe(null);
      setMfaPending(false);
    }
  }, []);

  const can = useCallback(
    (...permissions: string[]) => {
      if (!me) return false;
      if (me.permissions.includes("*")) return true;
      return permissions.every((p) => me.permissions.includes(p));
    },
    [me],
  );

  const value = useMemo(
    () => ({ me, loading, mfaPending, login, verifyMfa, resumeMfa, logout, refresh, can }),
    [me, loading, mfaPending, login, verifyMfa, resumeMfa, logout, refresh, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
